import type { Database_Procs_GetAlgebraGoalsMetric_Item } from '@suntex.fim/api-client';
import {
  $GoalsIndexPhase,
  GoalsIndexPhase,
  SpecialGradeLevel,
} from '@suntex.fim/enums';
import { currentFimYear } from '@suntex.fim/utils';

import type { DropdownFilters } from './types';
import { SectorConfig } from './types';

// Common chart dimensions
export const CHART_DIMENSIONS = {
  width: 225,
  height: 225,
  radius: 100,
} as const;

/** Option shape for phase-level select components */
export type PhaseLevelOption = {
  key: GoalsIndexPhase;
  label: string;
};

export const goalsIndexPhaseOptions: readonly PhaseLevelOption[] =
  $GoalsIndexPhase.listOptions();

/** Default dropdown filter state for Goals Index report pages */
export const defaultDropdownFilter: DropdownFilters = {
  grade: SpecialGradeLevel.AllGrades,
  year: currentFimYear({ now: new Date() }),
};

// Define games with their properties as sectors
export const algebraGoalsIndexSectors: SectorConfig<Database_Procs_GetAlgebraGoalsMetric_Item>[] =
  [
    {
      name: 'Algebra Overall',
      description:
        'Measures students’ ability to manipulate algebraic expressions using the six foundational skills: Fact Fluency, Fractions, Decimals, Integers, Exponents, and Order of Operations.',
      scorePropertyName: 'algebraOverall',
      weight: 16,
      color: '#6250EF',
    },
    {
      name: 'VIFs',
      description:
        'Practice with fact fluency using the 16 Very Important Facts(VIFs) for Addition and Subtraction; 36 VIFs for Multiplication and Division.',
      scorePropertyName: 'factFluency',
      weight: 8,
      color: '#6250EF',
    },
    {
      name: 'Equivalent Fractions',
      description:
        'Practice with equivalent fractions using 1/32, 1/30, 1/28, 1/27, 1/25, 1/24, 1/20, 1/18, 1/16, 1/14, 1/12, 1/10, 1/9, 1/8, 1/7, 1/6, 1/5, 1/4, 1/3, 3/8, 1/2, 5/8, 3/4, 7/8, 1',
      scorePropertyName: 'equivalentFractions',
      weight: 8,
      color: '#6250EF',
    },
    {
      name: 'Fractions',
      description:
        'Practice adding, subtracting, multiplying, and dividing fractions using 1/32, 1/16, 1/10, 1/9, 1/8, 1/5, 1/3, 1/4, 1/2, and whole numbers up to 100.',
      scorePropertyName: 'fractions',
      weight: 8,
      color: '#6250EF',
    },
    {
      name: 'Decimals',
      description:
        'Practice adding, subtracting, multiplying, and dividing decimals using hundredths, tenths, ones, tens, and hundreds.',
      scorePropertyName: 'decimals',
      weight: 12,
      color: '#6250EF',
    },
    {
      name: 'Integers',
      description:
        'Practice adding, subtracting, multiplying, and dividing integers using positive and negative whole numbers from 1 to 100.',
      scorePropertyName: 'integers',
      weight: 16,
      color: '#6250EF',
    },
    {
      name: 'Exponents',
      description:
        'Practice adding, subtracting, multiplying, and dividing positive and negative whole numbers from 1 to 216 using exponents operations of squares, square roots, cubes, and cube roots.',
      scorePropertyName: 'exponents',
      weight: 16,
      color: '#6250EF',
    },
    {
      name: 'Order of Operations',
      description:
        'Practice adding, subtracting, multiplying, and dividing positive and negative whole numbers from 1 to 100 using PEMDAS: Parentheses, Exponents, Multiplication, and Division(from left to right), Addition and Subtraction(from left to right).',
      scorePropertyName: 'orderOfOperations',
      weight: 16,
      color: '#6250EF',
    },
  ];
